// apps/client/src/pages/pipeline-tracker/tabs/AnalystViewSection.tsx
//
// Operations > Overview, section 5: the Analyst view.
//
// WHO IS AN ANALYST. Salestrekker has no analyst field. A practice marks the credit analyst on a
// file by putting the analyst's name on the deal as a LABEL (Evoke, Matt Spears, 7 Oct 2026), and
// the deal's owner stays the broker. So an analyst here is a label the practice has named, saved on
// the broker roster beside who is a broker, and nothing is inferred: until a label is named the
// section shows one setup line and no figure. Because an analyst is never a deal owner, naming one
// cannot move anybody in or out of a broker table.
//
// WHAT THE FIGURES ARE. The same rules as the Pipeline tab, from the same Performance snapshot: In
// flight is the Pipeline tab's In flight, and submissions, approvals and settlements are the dated
// events Deal movements counts, over the twelve months ending at the snapshot. A deal labelled with
// two analysts counts for each, so the rows can add up to more than the total row, and the page
// says by how much.

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Lock, Plus, Tags, Trash2, UserSearch } from 'lucide-react';
import type { AnalystFigures, AnalystViewData, NoSnapshot } from '../../../hooks/pipelineTracker/useOperationsOverview';
import { useBrokerRoster, type RosterAnalyst } from '../../../hooks/pipelineTracker/useBrokerRoster';
import { Honesty, Pill, money, num } from './pipelineParts';
import { ROW_HOVER, TonePanel, prettyDate } from './operationsParts';

const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);

/** The figure columns, in reading order: what is on the desk now, then what moved through it. */
const COLUMNS: ReadonlyArray<{ key: string; label: string; cell: (f: AnalystFigures) => string }> = [
  { key: 'inFlight', label: 'In flight', cell: (f) => num(f.inFlight) },
  { key: 'inFlightValue', label: 'In flight value', cell: (f) => money(f.inFlightValueCents) },
  { key: 'submissions', label: 'Submitted', cell: (f) => num(sum(f.submissions)) },
  { key: 'approvals', label: 'Approved', cell: (f) => num(sum(f.approvals)) },
  { key: 'settlements', label: 'Settled', cell: (f) => num(sum(f.settlements)) },
  { key: 'settledValue', label: 'Settled value', cell: (f) => money(f.settledValueCents) },
];

const FigureCells: React.FC<{ figures: AnalystFigures; strong?: boolean }> = ({ figures, strong }) => (
  <>
    {COLUMNS.map((c) => (
      <td key={c.key} className={`py-2 pr-3 text-right tabular-nums ${strong ? 'font-semibold text-gray-100' : 'text-gray-200'}`}>
        {c.cell(figures)}
      </td>
    ))}
  </>
);

const AnalystTable: React.FC<{ data: AnalystViewData }> = ({ data }) => (
  <div className="overflow-x-auto" data-testid="analyst-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-gray-400">
          <th className="pb-2 pl-2 font-medium">Analyst</th>
          {COLUMNS.map((c) => (
            <th key={c.key} className="pb-2 pr-3 text-right font-medium">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.analysts.map((row) => (
          <tr key={row.label} className={`border-t border-gray-700/50 odd:bg-white/[0.015] ${ROW_HOVER}`}>
            <td className="py-2 pl-2 text-gray-200">
              {row.name}
              {row.name.toLowerCase() !== row.label.toLowerCase() && (
                <span className="ml-1.5 text-xs text-gray-500">label {row.label}</span>
              )}
            </td>
            <FigureCells figures={row} />
          </tr>
        ))}
        <tr className="border-t border-gray-600">
          <td className="py-2 pl-2 font-semibold text-gray-100">Any analyst</td>
          <FigureCells figures={data.labelled} strong />
        </tr>
        <tr className="border-t border-gray-700/50">
          <td className="py-2 pl-2 text-gray-400">No analyst label</td>
          <FigureCells figures={data.unlabelled} />
        </tr>
      </tbody>
    </table>
  </div>
);

/**
 * Naming the labels that mark an analyst. Admins only, like every roster edit.
 *
 * A draft the admin edits and saves once, rather than a save per keystroke: a label and a name are
 * typed together, and a half-typed label saved as an analyst would briefly move deals between rows.
 */
export const AnalystLabelsEditor: React.FC<{
  companyId: string;
  availableLabels: AnalystViewData['availableLabels'];
  onSaved: () => void;
}> = ({ companyId, availableLabels, onSaved }) => {
  const roster = useBrokerRoster(companyId, true);
  const [draft, setDraft] = useState<RosterAnalyst[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Reset the draft whenever the stored list changes (first load, a save, a conflict reload).
  useEffect(() => {
    setDraft(roster.analysts.map((a) => ({ label: a.label, name: a.name ?? '' })));
  }, [roster.analysts]);

  const taken = useMemo(() => new Set(draft.map((a) => a.label.trim().toLowerCase())), [draft]);
  const suggestions = availableLabels.filter((l) => !taken.has(l.label.toLowerCase()));

  const update = (index: number, patch: Partial<RosterAnalyst>) => {
    setSaved(false);
    setDraft((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  const save = async () => {
    setProblem(null);
    const outcome = await roster.setAnalysts(draft);
    if (outcome.ok) {
      setSaved(true);
      onSaved();
    } else {
      setProblem(outcome.message ?? 'That change could not be saved.');
    }
  };

  if (roster.loading && !roster.ready) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading the analyst list
      </p>
    );
  }

  const busy = roster.saving === '__analysts__';
  const listId = `analyst-labels-${companyId}`;

  return (
    <div data-testid="analyst-editor">
      <p className="mb-3 text-xs text-gray-500">
        Each analyst is the Salestrekker label your team puts on a deal the analyst is working. The
        label is matched as a whole word, in any case. This changes no other figure on the page:
        the deal owner is still the broker.
      </p>
      {roster.error && <p className="mb-3 text-sm text-red-400">{roster.error}</p>}
      {problem && <p className="mb-3 text-sm text-red-400">{problem}</p>}

      <datalist id={listId}>
        {suggestions.map((l) => (
          <option key={l.label} value={l.label}>{`${num(l.deals)} deals`}</option>
        ))}
      </datalist>

      <div className="flex flex-col gap-2">
        {draft.map((analyst, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <input
              value={analyst.label}
              list={listId}
              onChange={(e) => update(index, { label: e.target.value })}
              placeholder="Label on the deal"
              aria-label={`Label for analyst ${index + 1}`}
              maxLength={120}
              className="min-w-0 flex-1 rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            />
            <input
              value={analyst.name ?? ''}
              onChange={(e) => update(index, { name: e.target.value })}
              placeholder="Name to show (optional)"
              aria-label={`Name for analyst ${index + 1}`}
              maxLength={120}
              className="min-w-0 flex-1 rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setDraft((prev) => prev.filter((_, i) => i !== index));
              }}
              aria-label={`Remove analyst ${index + 1}`}
              className="text-gray-500 hover:text-red-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {suggestions.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[0.6875rem] text-gray-500">Labels on deals in the latest export:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.slice(0, 30).map((l) => (
              <button
                key={l.label}
                type="button"
                onClick={() => {
                  setSaved(false);
                  setDraft((prev) => [...prev, { label: l.label, name: '' }]);
                }}
                className="inline-flex items-center gap-1 rounded-full border border-gray-600 bg-gray-900/60 px-2.5 py-0.5 text-xs text-gray-300 hover:bg-blue-500/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              >
                <Plus className="h-3 w-3" aria-hidden="true" />
                {l.label}
                <span className="text-gray-500">{num(l.deals)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setSaved(false);
            setDraft((prev) => [...prev, { label: '', name: '' }]);
          }}
          className="rounded-lg border border-gray-600 bg-gray-900 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-700/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
        >
          Add a label by hand
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || !roster.ready}
          className="rounded-lg border border-blue-500/50 bg-blue-600/20 px-3 py-1.5 text-xs font-semibold text-blue-200 hover:bg-blue-600/30 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
        >
          Save analysts
        </button>
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
        {!busy && saved && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" aria-label="Saved" />}
      </div>
    </div>
  );
};

export interface AnalystViewSectionProps {
  companyId: string;
  /** The response's analyst part; undefined when the server predates it. */
  data: (({ status: 'ok' } & AnalystViewData) | NoSnapshot) | undefined;
  /** super_admin or administrator, as for every roster edit. */
  canEdit: boolean;
  onSaved: () => void;
}

const AnalystViewSection: React.FC<AnalystViewSectionProps> = ({ companyId, data, canEdit, onSaved }) => {
  if (data === undefined || data.status !== 'ok') {
    return (
      <TonePanel title="Analyst view" tone="brand" icon={UserSearch}>
        <p className="text-sm text-gray-400">
          {data === undefined
            ? 'The analyst view is not available yet. Refresh the page to load it.'
            : 'No Performance export has been published yet. The analyst view reads its deal labels.'}
        </p>
      </TonePanel>
    );
  }

  const editor = canEdit ? (
    <details className="group mt-4" open={!data.configured}>
      <summary className="cursor-pointer list-none text-[0.8125rem] text-gray-400 hover:text-gray-200">
        <span className="inline-flex items-center gap-1.5">
          <Tags className="h-3.5 w-3.5" aria-hidden="true" />
          {data.configured ? 'Edit analyst labels' : 'Name the labels that mark an analyst'}
        </span>
      </summary>
      <div className="mt-3">
        <AnalystLabelsEditor companyId={companyId} availableLabels={data.availableLabels} onSaved={onSaved} />
      </div>
    </details>
  ) : null;

  if (!data.configured) {
    return (
      <TonePanel title="Analyst view" tone="brand" icon={UserSearch}>
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-100"
          data-testid="analyst-not-set-up"
        >
          <Tags className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            The analyst view starts once the Salestrekker labels that mark an analyst on a deal are named.
          </span>
        </div>
        {editor ?? (
          <span className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
            <Lock className="h-3 w-3" />
            Only an administrator can name the analyst labels.
          </span>
        )}
      </TonePanel>
    );
  }

  return (
    <TonePanel
      title="Analyst view"
      tone="brand"
      icon={UserSearch}
      badge={<Pill tone="good">Live</Pill>}
      blurb={`Deals carrying each analyst's label in the Performance export as at ${prettyDate(data.asAtDate)}. In flight is the Pipeline tab's In flight; submitted, approved and settled are the same dated events as Deal movements, ${prettyDate(data.from)} to ${prettyDate(data.to)}.`}
    >
      <AnalystTable data={data} />
      <div className="mt-3 space-y-2">
        {data.sharedDeals > 0 && (
          <Honesty>
            {num(data.sharedDeals)} {data.sharedDeals === 1 ? 'deal carries' : 'deals carry'} more than one
            analyst&apos;s label and {data.sharedDeals === 1 ? 'counts' : 'count'} for each, so the analyst rows
            add up to more than Any analyst.
          </Honesty>
        )}
        {data.unseenLabels.length > 0 && (
          <Honesty>
            No deal in this export carries the label{data.unseenLabels.length === 1 ? '' : 's'}{' '}
            <b className="font-semibold text-gray-300">{data.unseenLabels.join(', ')}</b>. Check the spelling
            matches the label in Salestrekker.
          </Honesty>
        )}
      </div>
      {editor}
    </TonePanel>
  );
};

export default AnalystViewSection;
